// Document types based on Vesta DocumentsClient and borrower-facing document API.

/**
 * The Vesta `DocumentStatus` enum, verbatim — AFN.Core.VestaClient 26.2.1
 * (`AFN.Core.VestaClient.Models.Enums.DocumentStatus`: Uploaded=0, Reviewed=1,
 * Accepted=2, Archived=3, Expired=4).
 *
 * PATH.Api passes the enum *name* straight through
 * (`WizardDocuments.cs:84` — `d.Status.Value?.ToString() ?? d.Status.Raw`),
 * so these PascalCase strings are exactly what lands on the wire. `Archived`
 * is filtered out server-side for loan documents (`WizardDocuments.cs:78`) but
 * is still mapped here — borrower-level documents don't go through that filter.
 */
export const VESTA_DOCUMENT_STATUSES = ['Uploaded', 'Reviewed', 'Accepted', 'Archived', 'Expired'] as const;
export type VestaDocumentStatus = (typeof VESTA_DOCUMENT_STATUSES)[number];

export type DocumentStatus = VestaDocumentStatus | 'Pending' | 'Rejected' | 'Verified' | 'Signed' | string;

export interface DocumentEntity {
  entityId: string;
  entityType: string;
}

export interface DocumentMetadata {
  /** Row id — unique per rendered row (composite `docId::fileId` for multi-file Vesta docs). */
  id: string;
  loanId: string;
  /** Wire document id for wizard download/preview (falls back to `id` when absent). */
  documentId?: string;
  /** Vesta file id within the document — targets an exact file on download. */
  fileId?: string;
  /** Borrower-facing display name. New /api/v1 field. */
  name?: string;
  /** Borrower-facing category. New /api/v1 field. */
  category?: string;
  /** Size in bytes. New /api/v1 field. */
  size?: number;
  /** Upload timestamp. New /api/v1 field. */
  uploadedAt?: string;
  /** Who uploaded the latest file — raw wire value (borrower email or "System");
      loan (Vesta) documents don't carry attribution yet (NP-423 API gap). */
  uploadedBy?: string | null;
  status: DocumentStatus;
  createdAt?: string;
  updatedAt?: string;
  documentTypeName?: string;
  documentTypeId?: string;
  nameOverride?: string;
  fileName?: string;
  fileSize?: number;
  contentType?: string;
  associatedEntities?: DocumentEntity[];
}

export interface DocumentUploadInput {
  /** New borrower API category field. */
  category?: string;
  /** Backward-compatible Vesta metadata field. */
  documentTypeName?: string;
  documentTypeId?: string;
  nameOverride?: string;
  description?: string;
  associatedEntities?: DocumentEntity[];
  documentRequiredTaskIds?: string[];
}

export interface DocumentUploadResponse {
  documentId: string;
  id?: string;
  fileName?: string;
  uploadedAt?: string;
}

export interface DocumentListResult {
  documents: DocumentMetadata[];
  totalCount: number;
}

export interface DocumentAccessResponse {
  url: string;
  expiresAt: string;
  contentType?: string;
  fileName?: string;
}

export function getDocumentName(document: DocumentMetadata): string {
  return document.name || document.nameOverride || document.fileName || document.documentTypeName || 'Untitled document';
}

export function getDocumentCategory(document: DocumentMetadata): string {
  return document.category || document.documentTypeName || 'Other';
}

export function getDocumentSize(document: DocumentMetadata): number | undefined {
  return document.size ?? document.fileSize;
}

export function getDocumentUploadedAt(document: DocumentMetadata): string | undefined {
  return document.uploadedAt || document.createdAt || document.updatedAt;
}

/** Visual weight for a status badge — maps to the semantic colour tokens in index.css. */
export type DocumentStatusTone = 'success' | 'info' | 'warning' | 'danger' | 'muted';

export interface DocumentStatusPresentation {
  /** Canonical state. `Unknown` = a value we do not recognise (label echoes the wire value). */
  key: VestaDocumentStatus | 'Pending' | 'Rejected' | 'Unknown';
  /** Borrower-facing label. */
  label: string;
  tone: DocumentStatusTone;
}

/**
 * Case-insensitive wire-value → badge presentation.
 *
 * Covers the full Vesta enum plus the non-Vesta values the wire can still carry:
 * `Pending` (borrower-level default, see `mapDocumentSummaryToMetadata`) and
 * `Rejected` / `Verified` / `Signed` (legacy + `Status.Raw` passthrough).
 */
const DOCUMENT_STATUS_PRESENTATION: Record<string, DocumentStatusPresentation> = {
  // ── Vesta DocumentStatus ────────────────────────────────────
  uploaded: { key: 'Uploaded', label: 'Uploaded', tone: 'warning' },
  reviewed: { key: 'Reviewed', label: 'Reviewed', tone: 'info' },
  accepted: { key: 'Accepted', label: 'Accepted', tone: 'success' },
  archived: { key: 'Archived', label: 'Archived', tone: 'muted' },
  expired: { key: 'Expired', label: 'Expired', tone: 'danger' },
  // ── non-Vesta wire values ───────────────────────────────────
  pending: { key: 'Pending', label: 'Pending', tone: 'warning' },
  rejected: { key: 'Rejected', label: 'Rejected', tone: 'danger' },
  // Pre-existing aliases that have always rendered as "Accepted".
  verified: { key: 'Accepted', label: 'Accepted', tone: 'success' },
  signed: { key: 'Accepted', label: 'Accepted', tone: 'success' },
  // Borrower-level docs can carry `APPROVED` (see src/mocks/data/documents.ts);
  // the previous `normalizeDocumentStatus` treated it as an acceptance.
  approved: { key: 'Accepted', label: 'Accepted', tone: 'success' },
};

/** `PendingReview` → `Pending Review`; leaves already-spaced values alone. */
function humanizeStatus(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * NP-427: resolves a document's wire status to its badge presentation.
 *
 * Previously the badge had cases for `accepted`/`verified`/`signed` and
 * `rejected` only, and *everything else* fell through to a hard-coded
 * "Pending" — so a document set to **Reviewed** in Vesta displayed as
 * "Pending" in PATH. Unrecognised values now echo the wire value instead of
 * being silently relabelled, so a future Vesta enum addition is visible rather
 * than quietly wrong.
 */
export function getDocumentStatusPresentation(status?: DocumentStatus | null): DocumentStatusPresentation {
  const raw = String(status ?? '').trim();
  // No status at all: nothing is known yet, so "Pending" is honest here.
  if (!raw) return DOCUMENT_STATUS_PRESENTATION.pending;
  return (
    DOCUMENT_STATUS_PRESENTATION[raw.toLowerCase()] ?? {
      key: 'Unknown',
      label: humanizeStatus(raw),
      tone: 'muted',
    }
  );
}

/**
 * Coarse review outcome, for logic that only cares whether a document is
 * settled. `IN_REVIEW` covers Uploaded/Reviewed/Pending — i.e. with the loan
 * team but not yet accepted. Note `Reviewed` is NOT an acceptance in Vesta.
 */
export function normalizeDocumentStatus(status?: DocumentStatus | null): 'IN_REVIEW' | 'ACCEPTED' | 'REJECTED' | 'INACTIVE' | 'UNKNOWN' {
  switch (getDocumentStatusPresentation(status).key) {
    case 'Accepted':
      return 'ACCEPTED';
    case 'Rejected':
      return 'REJECTED';
    case 'Archived':
    case 'Expired':
      return 'INACTIVE';
    case 'Uploaded':
    case 'Reviewed':
    case 'Pending':
      return 'IN_REVIEW';
    default:
      return 'UNKNOWN';
  }
}

/**
 * Best-effort MIME type from a file name, for callers that have no usable one on
 * the wire. Whether a given stored value counts as "usable" is the CALLER's
 * decision, not this function's — the task attachment card treats a stored
 * `application/octet-stream` as unknown and comes here
 * (`usableContentType` in hooks/useTasks.ts), while
 * {@link getDocumentContentType} below deliberately trusts whatever the
 * Documents surface recorded.
 */
export function contentTypeFromFileName(fileName: string): string {
  const name = fileName.toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.heic')) return 'image/heic';
  if (name.endsWith('.heif')) return 'image/heif';
  return 'application/octet-stream';
}

/** The extension a served content type implies — the inverse of the map above. */
const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'application/pdf': '.pdf',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/heic': '.heic',
  'image/heif': '.heif',
};

/**
 * The name to save a download under, reconciled with what the bytes actually are.
 *
 * Vesta normalises an uploaded photo to PDF on ingest but KEEPS the borrower's
 * original file name, so `test.jpg` in the list is a PDF on the wire. Saved under
 * the stored name the file is intact but unopenable — the OS picks its handler by
 * extension, and no image viewer will take a `%PDF-1.3` header. The real extension
 * is appended rather than swapped in: appending can never eat part of a name that
 * merely looks like it has an extension, and it matches Vesta's own `id.jpg.pdf`.
 */
export function downloadFileName(fileName: string, contentType: string): string {
  const extension = EXTENSION_BY_CONTENT_TYPE[contentType];
  // Nothing known to append, or the name already says what the bytes are.
  if (!extension || contentTypeFromFileName(fileName) === contentType) return fileName;
  return `${fileName}${extension}`;
}

/**
 * The Documents surface's own rule: a recorded content type wins outright, and
 * the file name is consulted only when there is none. Deliberately unchanged —
 * every caller here predates the task attachment card, and tightening it would
 * alter which documents that page offers a preview for, which is a Docs-side
 * decision rather than a side effect of the Tasks integration.
 */
export function getDocumentContentType(document: DocumentMetadata): string {
  if (document.contentType) return document.contentType;
  return contentTypeFromFileName(getDocumentName(document));
}
